import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface UiState {
  sidebarCollapsed: boolean;
  loading: boolean;
  theme: 'light' | 'dark';
  language: 'ru' | 'en';
  selectedWarehouse: number | null;
  modalStates: Record<string, boolean>;
  activeFilters: Record<string, any>;
}

const initialState: UiState = {
  sidebarCollapsed: false,
  loading: false,
  theme: 'light',
  language: 'ru',
  selectedWarehouse: null,
  modalStates: {},
  activeFilters: {},
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },

    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
    },

    setLanguage: (state, action: PayloadAction<'ru' | 'en'>) => {
      state.language = action.payload;
    },

    setSelectedWarehouse: (state, action: PayloadAction<number | null>) => {
      state.selectedWarehouse = action.payload;
    },

    setModalState: (state, action: PayloadAction<{ modal: string; isOpen: boolean }>) => {
      const { modal, isOpen } = action.payload;
      state.modalStates[modal] = isOpen;
    },

    closeAllModals: (state) => {
      Object.keys(state.modalStates).forEach(modal => {
        state.modalStates[modal] = false;
      });
    },

    setFilter: (state, action: PayloadAction<{ key: string; value: any }>) => {
      const { key, value } = action.payload;
      state.activeFilters[key] = value;
    },

    clearFilter: (state, action: PayloadAction<string>) => {
      delete state.activeFilters[action.payload];
    },

    clearAllFilters: (state) => {
      state.activeFilters = {};
    },

    setFilters: (state, action: PayloadAction<Record<string, any>>) => {
      state.activeFilters = action.payload;
    },
  },
});

export const {
  toggleSidebar,
  setSidebarCollapsed,
  setLoading,
  setTheme,
  setLanguage,
  setSelectedWarehouse,
  setModalState,
  closeAllModals,
  setFilter,
  clearFilter,
  clearAllFilters,
  setFilters,
} = uiSlice.actions;

export default uiSlice.reducer;

// Selectors
export const selectUi = (state: { ui: UiState }) => state.ui;
export const selectSidebarCollapsed = (state: { ui: UiState }) => state.ui.sidebarCollapsed;
export const selectLoading = (state: { ui: UiState }) => state.ui.loading;
export const selectTheme = (state: { ui: UiState }) => state.ui.theme;
export const selectLanguage = (state: { ui: UiState }) => state.ui.language;
export const selectSelectedWarehouse = (state: { ui: UiState }) => state.ui.selectedWarehouse;
export const selectModalState = (modal: string) => (state: { ui: UiState }) => 
  state.ui.modalStates[modal] || false;
export const selectActiveFilters = (state: { ui: UiState }) => state.ui.activeFilters; 